import React, { useState, useRef } from 'react';
import {
  Plus, Trash2, Save, ChevronUp, ChevronDown, Upload, Image as ImageIcon,
  Video, Loader2, FolderOpen, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const mediaUrl = (url) => (url && url.startsWith('/api') ? `${API_URL}${url}` : url);

function thumbFor(item) {
  if (item.type === 'video') {
    if (item.poster_url || item.thumbnail_url) return mediaUrl(item.poster_url || item.thumbnail_url);
    return 'https://placehold.co/300x300/202020/6F6A63?text=Video';
  }
  return mediaUrl(item.url);
}

/** Bearbeitungsformular für ein einzelnes Portal inkl. Galerie-Verwaltung */
function PortalCard({ portal, index, total, token, media, onRefresh, onMove }) {
  const [form, setForm] = useState({
    name: portal.name || '',
    tagline: portal.tagline || '',
    bio: portal.bio || '',
    email: portal.email || '',
    phone: portal.phone || '',
    instagram_url: portal.instagram_url || '',
    website_url: portal.website_url || '',
  });
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const imageInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };
  const portalMedia = media.filter((m) => m.portal_id === portal.id);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/portals/${portal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ ...form, active: portal.active, order: portal.order, ...extra }),
      });
      if (response.ok) {
        toast.success('Portfolio gespeichert');
        onRefresh();
      } else {
        toast.error('Speichern fehlgeschlagen');
      }
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (value) => {
    await fetch(`${API_URL}/api/portals/${portal.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ ...form, active: value, order: portal.order }),
    });
    onRefresh();
  };

  const remove = async () => {
    if (!window.confirm(`Portfolio "${portal.name}" wirklich löschen? Die zugeordneten Bilder bleiben in der Galerie erhalten.`)) return;
    const response = await fetch(`${API_URL}/api/portals/${portal.id}`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    if (response.ok) {
      toast.success('Portfolio gelöscht');
      onRefresh();
    } else {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const uploadPortalFile = async (kind, file, setBusy) => {
    if (!file) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/portals/${portal.id}/${kind}`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      });
      if (response.ok) {
        toast.success(kind === 'image' ? 'Profilbild hochgeladen' : 'Cover hochgeladen');
        onRefresh();
      } else {
        toast.error('Upload fehlgeschlagen');
      }
    } catch {
      toast.error('Upload fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const uploadGalleryImages = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      formData.append('category', '');
      formData.append('portal_id', portal.id);
      const response = await fetch(`${API_URL}/api/media/bulk-upload`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.success} Bild(er) hochgeladen${data.failed ? `, ${data.failed} fehlgeschlagen` : ''}`);
        onRefresh();
      } else {
        toast.error('Upload fehlgeschlagen');
      }
    } catch {
      toast.error('Upload fehlgeschlagen');
    } finally {
      setUploadingMedia(false);
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const addVideo = async () => {
    if (!videoUrl.trim()) {
      toast.error('Bitte Video-URL eingeben');
      return;
    }
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append('video_url', videoUrl.trim());
      formData.append('category', '');
      formData.append('portal_id', portal.id);
      const response = await fetch(`${API_URL}/api/media/video-upload`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      });
      if (response.ok) {
        toast.success('Video hinzugefügt');
        setVideoUrl('');
        onRefresh();
      } else {
        toast.error('Video konnte nicht hinzugefügt werden');
      }
    } catch {
      toast.error('Video konnte nicht hinzugefügt werden');
    } finally {
      setUploadingMedia(false);
    }
  };

  const removeMedia = async (id) => {
    const response = await fetch(`${API_URL}/api/media/${id}`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    if (response.ok) {
      toast.success('Medium gelöscht');
      onRefresh();
    } else {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {portal.image_url ? (
              <img src={mediaUrl(portal.image_url)} alt="" className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
            <div>
              <CardTitle className="text-foreground text-lg">{portal.name}</CardTitle>
              <p className="text-xs text-muted-foreground">
                /portfolio/{portal.slug} · {portalMedia.length} Medien
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onMove(index, -1)} disabled={index === 0} title="Nach oben">
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onMove(index, 1)} disabled={index === total - 1} title="Nach unten">
              <ChevronDown className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 mr-2">
              <Label className="text-xs text-muted-foreground">Aktiv</Label>
              <Switch checked={!!portal.active} onCheckedChange={toggleActive} />
            </div>
            <Button variant="outline" size="sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Zuklappen' : 'Bearbeiten'}
            </Button>
            <Button variant="ghost" size="sm" onClick={remove} className="text-red-500 hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-6">
          {/* Stammdaten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground text-sm">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border-border text-foreground" />
            </div>
            <div>
              <Label className="text-foreground text-sm">Tagline (im Panel)</Label>
              <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="z.B. Campaign · Portrait · Product" className="bg-background border-border text-foreground" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-foreground text-sm">Beschreibung (Detailseite)</Label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4} className="bg-background border-border text-foreground" />
            </div>
            <div>
              <Label className="text-foreground text-sm">E-Mail</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-background border-border text-foreground" />
            </div>
            <div>
              <Label className="text-foreground text-sm">Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-background border-border text-foreground" />
            </div>
            <div>
              <Label className="text-foreground text-sm">Instagram-URL</Label>
              <Input value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} placeholder="https://instagram.com/..." className="bg-background border-border text-foreground" />
            </div>
            <div>
              <Label className="text-foreground text-sm">Website-URL</Label>
              <Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://..." className="bg-background border-border text-foreground" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => save()} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>

            {/* Profilbild */}
            <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPortalFile('image', e.target.files[0], setUploadingImage)} />
            <Button variant="outline" onClick={() => imageInputRef.current?.click()} disabled={uploadingImage}>
              {uploadingImage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
              Profilbild
            </Button>

            {/* Cover (Hover-Bild im Panel) */}
            <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPortalFile('cover', e.target.files[0], setUploadingCover)} />
            <Button variant="outline" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover}>
              {uploadingCover ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Hover-Cover
            </Button>

            {portal.cover_url && (
              <img src={mediaUrl(portal.cover_url)} alt="Cover" className="h-10 w-16 object-cover border border-border" />
            )}
          </div>

          {/* Galerie */}
          <div className="border-t border-border pt-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h4 className="text-foreground font-semibold">Galerie ({portalMedia.length})</h4>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => uploadGalleryImages(e.target.files)}
                />
                <Button variant="outline" size="sm" onClick={() => galleryInputRef.current?.click()} disabled={uploadingMedia}>
                  {uploadingMedia ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Bilder hochladen
                </Button>
                <div className="flex items-center gap-2">
                  <Input
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="Video-URL (YouTube/Vimeo/MP4)"
                    className="bg-background border-border text-foreground h-9 w-56"
                  />
                  <Button variant="outline" size="sm" onClick={addVideo} disabled={uploadingMedia}>
                    <Video className="w-4 h-4 mr-1" /> Video
                  </Button>
                </div>
              </div>
            </div>

            {portalMedia.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Medien zugeordnet.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {portalMedia.map((item) => (
                  <div key={item.id} className="relative group aspect-square bg-muted overflow-hidden">
                    <img src={thumbFor(item)} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                    {item.type === 'video' && (
                      <div className="absolute bottom-1 left-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 uppercase">Video</div>
                    )}
                    <button
                      onClick={() => removeMedia(item.id)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full items-center justify-center hidden group-hover:flex"
                      title="Löschen"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function PortalsAdmin({ token, portals, media, onRefresh }) {
  const [newPortal, setNewPortal] = useState({ name: '', tagline: '' });
  const [creating, setCreating] = useState(false);

  const sorted = [...portals].sort((a, b) => (a.order || 0) - (b.order || 0));

  // Zwei benachbarte Portale tauschen (Reihenfolge)
  const movePortal = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sorted.length) return;
    const a = sorted[index];
    const b = sorted[target];
    try {
      await Promise.all([
        fetch(`${API_URL}/api/portals/${a.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: a.name, order: target }),
        }),
        fetch(`${API_URL}/api/portals/${b.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: b.name, order: index }),
        }),
      ]);
      onRefresh();
    } catch {
      toast.error('Reihenfolge konnte nicht geändert werden');
    }
  };

  const create = async () => {
    if (!newPortal.name.trim()) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/portals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: newPortal.name.trim(),
          tagline: newPortal.tagline.trim() || null,
          order: portals.length,
        }),
      });
      if (response.ok) {
        toast.success('Portfolio angelegt');
        setNewPortal({ name: '', tagline: '' });
        onRefresh();
      } else {
        toast.error('Anlegen fehlgeschlagen');
      }
    } catch {
      toast.error('Anlegen fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Neues Portfolio */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-lg">Neues Portfolio anlegen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-foreground text-sm">Name *</Label>
              <Input
                value={newPortal.name}
                onChange={(e) => setNewPortal({ ...newPortal, name: e.target.value })}
                placeholder="z.B. Foto, Video, Max Rempe ..."
                className="bg-background border-border text-foreground"
              />
            </div>
            <div>
              <Label className="text-foreground text-sm">Tagline</Label>
              <Input
                value={newPortal.tagline}
                onChange={(e) => setNewPortal({ ...newPortal, tagline: e.target.value })}
                placeholder="z.B. Campaign · Portrait · Product"
                className="bg-background border-border text-foreground"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={create} disabled={creating} className="w-full">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Portfolio anlegen
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Es können beliebig viele Portfolios angelegt werden. Auf der Portfolio-Seite
            wird bei mehr als drei Einträgen horizontal gescrollt (Mausrad oder Pfeile).
          </p>
        </CardContent>
      </Card>

      {/* Liste */}
      {sorted.map((portal, index) => (
        <PortalCard
          key={portal.id}
          portal={portal}
          index={index}
          total={sorted.length}
          token={token}
          media={media}
          onRefresh={onRefresh}
          onMove={movePortal}
        />
      ))}

      {sorted.length === 0 && (
        <p className="text-muted-foreground text-center py-8">Noch keine Portfolios angelegt.</p>
      )}
    </div>
  );
}
